// Los datos de la ficha del proceso, dibujados con su tipo (F26).
//
// Un solo componente para los dos lugares donde se escriben datos de un
// prospecto: el modal de alta del tablero y la sección Datos de la ficha. La
// forma del control la decide la ficha del proceso, igual que en la ficha de
// contacto: una lista se elige, una fecha se elige en un calendario y un texto
// se escribe. Antes el alta pedía un "Área de interés" de texto libre que no
// coincidía con nada de lo que el Agente captura, y la ficha no dejaba escribir
// nada en absoluto.

import { useRef } from 'react'

import type { LeadFieldDef } from './leads-data.js'
import { FormField, Input, Select } from '../../design-system/index.js'

/** Lo que se dice donde IRÍAN los datos cuando el proceso no define ninguno.
 *  Un hueco callado entre Nombre y Origen (o una sección Datos ausente) se lee
 *  como una pantalla rota; esto dice qué pasa y dónde se arregla. Vive aquí,
 *  junto al componente que dibuja el caso contrario, para que las dos pantallas
 *  digan exactamente lo mismo. */
export const EMPTY_FICHA_HINT =
  'Este proceso aún no define datos. Se configuran en Configuración → Procesos → Datos del proceso.'

/** Lo que se dice cuando la casilla está mostrando un valor que ya no está en
 *  la lista. Desde que las opciones pueden venir de una fuente viva (D21-1),
 *  archivar al último doctor de Cardiología saca 'Cardiología' de la lista sin
 *  que nadie toque a los prospectos que la traen. El valor se sigue pudiendo
 *  leer, guardar y volver a elegir; esta línea explica por qué está ahí abajo y
 *  no entre las demás. */
export const HISTORICAL_OPTION_HINT = 'Esta opción ya no está en la lista.'

/**
 * EL VALOR HISTÓRICO SE QUEDA EN LA LISTA MIENTRAS DURE LA FORMA (D21-2).
 *
 * Derivarlo solo del valor ACTUAL alcanzaba para dibujarlo, pero no para lo
 * que el PR promete: al mirar otra opción el histórico desaparecía del
 * desplegable y ya no se podía VOLVER a elegir, así que un vistazo curioso se
 * convertía en un cambio irreversible. Se recuerda por clave mientras la forma
 * está abierta, y se olvida si la lista viva lo vuelve a ofrecer (el doctor
 * regresó): ahí ya no es historia, es una opción como las demás.
 */
export function useHistoricalOptions(): (
  key: string,
  value: string,
  options: readonly string[],
) => string | null {
  const seen = useRef<Record<string, string>>({})
  return (key, value, options) => {
    if (value !== '' && !options.includes(value)) seen.current[key] = value
    const remembered = seen.current[key]
    if (remembered === undefined) return null
    if (options.includes(remembered)) {
      delete seen.current[key]
      return null
    }
    return remembered
  }
}

export function LeadFieldInputs({
  fields,
  values,
  onChange,
  idPrefix,
  disabled = false,
}: {
  fields: LeadFieldDef[]
  /** Valor actual por clave; una clave ausente se dibuja vacía. */
  values: Record<string, string>
  onChange: (key: string, value: string) => void
  /** Prefijo de los `id`, para que los dos formularios convivan en una página. */
  idPrefix: string
  disabled?: boolean
}): JSX.Element {
  const historicalFor = useHistoricalOptions()
  return (
    <>
      {fields.map((f) => {
        const id = `${idPrefix}-${f.key}`
        const value = values[f.key] ?? ''
        // El valor HISTÓRICO (D21-2): lo que la tarjeta trae guardado y la lista
        // viva ya no ofrece. Sin esto el Select lo dibuja como "Sin especificar"
        // y el primer guardado lo BORRA sin que nadie lo haya pedido; con esto
        // sigue elegible, que es lo que el servidor acepta para esta tarjeta.
        // En el alta nunca aparece: una tarjeta nueva no tiene valor actual.
        const historical =
          f.dataType === 'enum' ? historicalFor(f.key, value, f.options ?? []) : null
        return (
          <FormField
            key={f.key}
            label={f.label}
            htmlFor={id}
            required="optional"
            {...(historical !== null && historical === value
              ? { helper: HISTORICAL_OPTION_HINT }
              : {})}
          >
            {f.dataType === 'enum' ? (
              <Select
                id={id}
                disabled={disabled}
                options={[
                  // Vacío es una respuesta: "todavía no lo sé" tiene que poder
                  // decirse, y borrar un valor tiene que poder deshacerse.
                  { value: '', label: 'Sin especificar' },
                  ...(historical !== null ? [{ value: historical, label: historical }] : []),
                  ...(f.options ?? []).map((o) => ({ value: o, label: o })),
                ]}
                value={value}
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            ) : f.dataType === 'boolean' ? (
              <Select
                id={id}
                disabled={disabled}
                options={[
                  { value: '', label: 'Sin especificar' },
                  { value: 'true', label: 'Sí' },
                  { value: 'false', label: 'No' },
                ]}
                value={value}
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            ) : (
              <Input
                id={id}
                disabled={disabled}
                type={f.dataType === 'number' ? 'number' : f.dataType === 'date' ? 'date' : 'text'}
                value={value}
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
          </FormField>
        )
      })}
    </>
  )
}
